import { useMemo, useState, memo, useCallback, useRef, useEffect } from "react";
import { useFormikContext } from "formik";
import { Grid, FormLabel, FormControl, Box } from "@mui/material";
import {
    FormInput,
    PlacePicker,
    GoogleMap,
    PlacesAutocomplete,
} from "src/components";
import {
    authSelector,
    navigatorSelector,
} from "src/modules/auth/redux/authSlice";
import { useAppSelector } from "src/hooks";
import { enqueueSnackbar } from "notistack";
import { tripsAPI } from "src/modules/trips/redux/tripSlice";
import { TripRequestFormValues } from "../types";
import { ParsedAddress } from "src/components/forms/types";
import { getCurrentPickupLocation, OUT_OF_ZONE_ERROR } from "../helpers";
import TripPrograms from "./TripPrograms";
import AutoLocate from "./AutoLocate";
import { isValidCoord } from "src/utils/helpers";

interface ProcessingState {
    active?: boolean;
    pickUp?: boolean;
    dropOff?: boolean;
    message?: string;
}

interface Props {
    isOnDemand: boolean;
}

const LocationSelector = ({ isOnDemand = false }: Props) => {
    const geofenceChecked = useRef("");
    const locationChecked = useRef(false);

    const { config, client, places,  programs } =
        useAppSelector(authSelector);

    // todo: canAutoschedule and canAutoApproveRequests are now handled by the tdb hook, consider removing it here
    const { canRequestAnyPULocation, canRequestAnyDOLocation } = useAppSelector(
        (state) => ({
            canRequestAnyPULocation:
                state.auth.config?.ConnectPreferences?.CanRequestAnyPULocation,
            canRequestAnyDOLocation:
                state.auth.config?.ConnectPreferences?.CanRequestAnyDOLocation,
        })
    );

    const isNavigator = useAppSelector(navigatorSelector);
    const { values, validateField, setFieldValue } =
        useFormikContext<TripRequestFormValues>();
    const [checkGeoEligibility] =
        tripsAPI.useCheckGeographicEligibiltyMutation();

    const [getMapGeofence, { isLoading, data: mapGeofence }] =
        tripsAPI.useLazyGetMapGeofenceQuery();

    const [processing, setProcessing] = useState<ProcessingState>({
        active: false,
        pickUp: false,
        dropOff: false,
        message: "processing ...",
    });

    const pickUpSpots = useMemo(() => {
        const filtered = places?.filter((item) => item.isPickUpSpot) ?? [];
        return filtered.sort((a, b) => -b.name.localeCompare(a.name));
    }, [places]);

    const dropOffSpots = useMemo(() => {
        const filtered = places?.filter((item) => item.isDropOffSpot) ?? [];
        return filtered.sort((a, b) => -b.name.localeCompare(a.name));
    }, [places]);

    const pickUpCoords = useMemo(
        () => ({
            lat: values.pickUpPlace.latitude,
            lng: values.pickUpPlace.longitude,
        }),
        [values.pickUpPlace.latitude, values.pickUpPlace.longitude]
    );

    const dropOffCoords = useMemo(
        () => ({
            lat: values.dropOffPlace.latitude,
            lng: values.dropOffPlace.longitude,
        }),
        [values.dropOffPlace.latitude, values.dropOffPlace.longitude]
    );

    const hasHomeAddress = useMemo(() => {
        const home = isNavigator ? client.address : client.home;
        return Boolean(home?.address1);
    }, [isNavigator, client.address, client.home]);

    const agencyCenter = useMemo(() => {
        if (
            !hasHomeAddress &&
            !isValidCoord(pickUpCoords) &&
            !isValidCoord(dropOffCoords) &&
            config?.Location?.Latitude &&
            config?.Location?.Longitude
        ) {
            return {
                lat: config.Location.Latitude,
                lng: config.Location.Longitude,
            };
        }
    }, [hasHomeAddress, pickUpCoords, dropOffCoords, config]);

    const mapZoom = agencyCenter ? 14 : 15;

    const getActiveZones = useCallback(
        (programName: string) => {
            const list = isNavigator ? programs : client?.programs;
            const program = list?.find(
                (program) => program.programName === programName
            );
            return program ? program?.activeGeozoneIds : [];
        },
        [isNavigator, client.programs, programs]
    );

    async function handleGeoEligibilityCheck(
        latitude: number,
        longitude: number,
        type: keyof ProcessingState = "pickUp"
    ) {
        const activeZones = getActiveZones(values.tripProgram);
        // if we dont have active zones but a check was requested, return true
        if (!activeZones?.length && latitude && longitude) return true;

        // if coords are invalid and zones are active return false
        if (activeZones?.length && (!latitude || !longitude)) return false;
        setProcessing({ [type]: true });
        try {
            // todo: find a way to call passioTransit with onDemandAccessToken instead of passio access for security reasons
            const response = await checkGeoEligibility({
                latitude,
                longitude,
            }).unwrap();
            const zonePoints = response.map((zone) => zone.id);
            const isInZone = activeZones.some((zone) =>
                zonePoints.includes(zone)
            );
            setProcessing({ [type]: false });
            return isInZone;
        } catch (error) {
            enqueueSnackbar(
                "Sorry that address is probably not in our service area",
                { variant: "error" }
            );
        }
        setProcessing({ [type]: false });
    }

    // check eligibility if trip program has active zones
    async function validateAddress(
        lat: number,
        lng: number,
        type: keyof ProcessingState = "pickUp"
    ) {
        let error = "";
        if (!isValidCoord({ lat, lng })) return error;

        const isInZone = await handleGeoEligibilityCheck(lat, lng, type);
        if (!isInZone) {
            error = OUT_OF_ZONE_ERROR;
        }
        return error;
    }

    const handleLoadGeoFence = useCallback(
        async (programName: string) => {
            try {
                const activeZones = getActiveZones(programName);
                // if (!activeZones.length) return;
                // allow map to refresh if zones are empty and get cleared
                await getMapGeofence(activeZones).unwrap();
            } catch {}
        },
        [getActiveZones, getMapGeofence]
    );

    async function handleTripProgram(value: string) {
        // refresh geofence then recheck addresses
        await handleLoadGeoFence(value);
        // revalidate form, time fields are dependent on trip programs
        await validateField("apptTime");
        await validateField("pickUpPlace");
        await validateField("dropOffPlace");
    }

    const loadPickUpLocation = useCallback(async () => {
        try {
            const address = await getCurrentPickupLocation();
            if (!address) return;
            setFieldValue("pickUpPlace", address);
        } catch (err) {
            enqueueSnackbar(
                "We couldn't get your current position. Defaults have been used instead",
                { variant: "info" }
            );
        }
    }, [setFieldValue]);

    useEffect(() => {
        if (geofenceChecked.current !== values.tripProgram) {
            handleLoadGeoFence(values.tripProgram);
            geofenceChecked.current = values.tripProgram;
        }
    }, [values.tripProgram, handleLoadGeoFence]);

    useEffect(() => {
        if (!isNavigator || !canRequestAnyPULocation) {
            locationChecked.current = true;
            return;
        }
        if (locationChecked.current) return;
        setProcessing({ pickUp: true });
        loadPickUpLocation();
        setProcessing({ pickUp: false });
        locationChecked.current = true;
    }, [canRequestAnyPULocation, isNavigator, loadPickUpLocation]);

    return (
        <Grid
            container
            item
            sx={{
                mb: 4,
                boxShadow: { xs: 0, md: 1 },
                borderRadius: { xs: 0, md: 1 },
            }}
        >
            <Grid
                item
                xs={12}
                md={6}
                sx={{
                    p: { md: 2 },
                    mb: { xs: 4, md: 0 },
                    display: "grid",
                }}
            >
                {/* Show custom place picker if user cannot request all locations */}
                {/* Pickup Address */}
                <FormControl
                    component="fieldset"
                    sx={{ mb: 4, display: "flex", gap: 2 }}
                >
                    <FormLabel
                        component="legend"
                        sx={{
                            mb: 2,
                            fontSize: 20,
                            color: "primary.main",
                        }}
                    >
                        Pick Up {!canRequestAnyPULocation ? "Spot" : "Address"}
                    </FormLabel>

                    {!canRequestAnyPULocation ? (
                        <PlacePicker
                            label="Address"
                            id="pickUpPlace"
                            name="pickUpPlace"
                            options={pickUpSpots}
                            sx={{ flexGrow: 1 }}
                        />
                    ) : (
                        <AutoLocate name="pickUpPlace">
                            <PlacesAutocomplete
                                label="Address"
                                id="pickUpPlace"
                                name="pickUpPlace"
                                loading={processing.pickUp}
                                sx={{ flex: 1 }}
                                validate={(address: ParsedAddress) => {
                                    return validateAddress(
                                        address.latitude,
                                        address.longitude,
                                        "pickUp"
                                    );
                                }}
                            />
                        </AutoLocate>
                    )}

                    {!isOnDemand && (
                        <FormInput
                            multiline
                            name="pickUpAddress2"
                            label="Apt/Suite/Notes"
                        />
                    )}
                </FormControl>

                {/* DropOff address */}
                <FormControl
                    component="fieldset"
                    sx={{ mb: 4, display: "flex", gap: 2 }}
                >
                    <FormLabel
                        component="legend"
                        sx={{
                            mb: 2,
                            fontSize: 20,
                            color: "primary.main",
                        }}
                    >
                        Drop Off {!canRequestAnyDOLocation ? "Spot" : "Address"}
                    </FormLabel>
                    {!canRequestAnyDOLocation ? (
                        <PlacePicker
                            label="Address"
                            id="dropOffPlace"
                            name="dropOffPlace"
                            options={dropOffSpots}
                            sx={{ flexGrow: 1 }}
                        />
                    ) : (
                        <AutoLocate name="dropOffPlace">
                            <PlacesAutocomplete
                                label="Address"
                                id="dropOffPlace"
                                name="dropOffPlace"
                                loading={processing.dropOff}
                                sx={{ flex: 1 }}
                                validate={(address: ParsedAddress) => {
                                    return validateAddress(
                                        address.latitude,
                                        address.longitude,
                                        "dropOff"
                                    );
                                }}
                            />
                        </AutoLocate>
                    )}

                    {!isOnDemand && (
                        <FormInput
                            multiline
                            name="dropOffAddress2"
                            label="Apt/Suite/Notes"
                        />
                    )}
                </FormControl>

                {/* Trip program */}
                <Box sx={{ ...(isOnDemand && { order: -1, mb: 2 }) }}>
                    <TripPrograms
                        loading={processing.active}
                        geofence={mapGeofence}
                        onChange={(evt) => {
                            handleTripProgram(evt.target.value);
                        }}
                    />
                </Box>
            </Grid>
            {/* map view */}
            <Grid item xs={12} md={6} mb={3}>
                <GoogleMap
                    loading={isLoading}
                    geofence={mapGeofence}
                    pickUpCoords={pickUpCoords}
                    dropOffCoords={dropOffCoords}
                    center={agencyCenter}
                    zoom={mapZoom}
                    sx={{ height: 450, width: "100%" }}
                />
            </Grid>
        </Grid>
    );
};

export default memo(LocationSelector);
